/**
 * DashboardSideMenu — persistent fixed-column dashboard navigation
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX3 · Feb 2026).
 *
 * FIX3 changes:
 *   • `Racket Lab` and `Mi perfil` are NOW static section titles whose
 *     submenus are ALWAYS visible (never collapsed). The section labels
 *     are non-interactive (`role="presentation"`).
 *   • New root entry `Panel` above `Racket Lab` — interactive link to
 *     `/app`, the universal return point used to replace the
 *     per-screen `Volver al panel` buttons.
 *   • Per-profile visibility driven by a single declarative
 *     `PROFILE_VISIBILITY` map (see below). Extensible for the next
 *     wave of profiles (operator/stringer/shop_manager/etc.) without
 *     touching layout code.
 *
 * Visibility rules (declarative)
 *   • `PROFILE_VISIBILITY[profile_key]` decides which root entries +
 *     subsections appear. A `*` (wildcard) defines the default for any
 *     profile not listed explicitly. STAFF roles (admin/staff/QA/observer)
 *     still hide `Mis recompensas`.
 *   • Routes that the profile can't reach also stay closed at the
 *     `ProtectedRoute`/page-level redirect — the side menu is a UX
 *     filter, not the security boundary.
 *
 * Frontend-only · preview/demo · no backend calls · no DB writes.
 */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import {
  Home, Wrench, CalendarDays, Package, Heart, Share2, Inbox,
  Settings as SettingsIcon, Award, QrCode, Store, ListChecks,
} from "lucide-react";

const STAFF_ROLE_KEYS = new Set([
  "admin", "staff", "qa_manager", "qa_tester", "institutional_observer",
]);

// Sidebar i18n labels — EN/ES authoritative; other 11 active languages
// covered with the same nesting for FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1.
// Anything not declared in a specific language falls back to EN via
// `T[lang] || T.en` at the consumer (`useLang`).
const T = {
  es: {
    panelEyebrow: "Panel",
    panel: "Panel",
    racketLab: "Racket Lab",
    myStringings: "Mis encordados",
    reservation: "Reserva de encordados",
    myGear: "Mi equipación",
    myQr: "Mi QR",
    myCalendar: "Mi calendario",
    myQuestionnaires: "Mis cuestionarios",
    wishlists: "Lista de deseos",
    referrals: "Mis referidos",
    requestsHub: "Hub de solicitudes",
    myRewards: "Mis recompensas",
    myProfile: "Mi perfil",
    opsView: "Vista operativa",
    profile: {
      account: "Cuenta",
      calendar: "Calendario",
      notifications: "Notificaciones",
      socialNetworks: "Redes sociales",
      familyRelations: "Familia y relaciones",
      favorites: "Favoritos",
      hobbies: "Aficiones e intereses",
      friendsNetwork: "Amigos y red",
      messaging: "Mensajería",
      wishlistPrefs: "Preferencias de wishlist",
      travelPrefs: "Preferencias de viaje",
      profileData: "Datos del perfil",
      professionalPrefs: "Preferencias profesionales",
      operationalPrefs: "Preferencias operativas",
      qaPrefs: "Preferencias QA",
      adminPrefs: "Preferencias admin",
      observerPrefs: "Preferencias de observación",
    },
    comingSoon: "Próximamente",
  },
  en: {
    panelEyebrow: "Panel",
    panel: "Panel",
    racketLab: "Racket Lab",
    myStringings: "My stringings",
    reservation: "Stringing booking",
    myGear: "My gear",
    myQr: "My QR",
    myCalendar: "My calendar",
    myQuestionnaires: "My questionnaires",
    wishlists: "Wishlists",
    referrals: "My referrals",
    requestsHub: "Requests hub",
    myRewards: "My rewards",
    myProfile: "My profile",
    opsView: "Operational view",
    profile: {
      account: "Account",
      calendar: "Calendar",
      notifications: "Notifications",
      socialNetworks: "Social networks",
      familyRelations: "Family & relationships",
      favorites: "Favorites",
      hobbies: "Hobbies & interests",
      friendsNetwork: "Friends & network",
      messaging: "Messaging",
      wishlistPrefs: "Wishlist preferences",
      travelPrefs: "Travel preferences",
      profileData: "Profile data",
      professionalPrefs: "Professional preferences",
      operationalPrefs: "Operational preferences",
      qaPrefs: "QA preferences",
      adminPrefs: "Admin preferences",
      observerPrefs: "Observer preferences",
    },
    comingSoon: "Coming soon",
  },
  // FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1: native localization for the
  // top sections + key submenu labels. Missing keys fall back to EN.
  it: {
    panel: "Pannello", racketLab: "Racket Lab", myStringings: "Le mie incordature", reservation: "Prenotazione incordatura", myGear: "La mia attrezzatura", myQr: "Il mio QR",
    myCalendar: "Il mio calendario", myQuestionnaires: "I miei questionari", wishlists: "Lista dei desideri", referrals: "I miei referral", requestsHub: "Hub richieste", myRewards: "Le mie ricompense",
    myProfile: "Il mio profilo", opsView: "Vista operativa", profile: { account: "Account", calendar: "Calendario", notifications: "Notifiche", socialNetworks: "Reti sociali", familyRelations: "Famiglia e relazioni", favorites: "Preferiti", hobbies: "Hobby e interessi", friendsNetwork: "Amici e rete", messaging: "Messaggi", wishlistPrefs: "Preferenze wishlist", travelPrefs: "Preferenze di viaggio", profileData: "Dati del profilo", professionalPrefs: "Preferenze professionali", operationalPrefs: "Preferenze operative", qaPrefs: "Preferenze QA", adminPrefs: "Preferenze admin", observerPrefs: "Preferenze osservatore" }, comingSoon: "Prossimamente",
  },
  fr: {
    panel: "Tableau de bord", racketLab: "Racket Lab", myStringings: "Mes cordages", reservation: "Réservation cordage", myGear: "Mon équipement", myQr: "Mon QR",
    myCalendar: "Mon calendrier", myQuestionnaires: "Mes questionnaires", wishlists: "Liste de souhaits", referrals: "Mes parrainages", requestsHub: "Centre de demandes", myRewards: "Mes récompenses",
    myProfile: "Mon profil", opsView: "Vue opérationnelle", profile: { account: "Compte", calendar: "Calendrier", notifications: "Notifications", socialNetworks: "Réseaux sociaux", familyRelations: "Famille et relations", favorites: "Favoris", hobbies: "Loisirs et intérêts", friendsNetwork: "Amis et réseau", messaging: "Messagerie", wishlistPrefs: "Préférences wishlist", travelPrefs: "Préférences de voyage", profileData: "Données du profil", professionalPrefs: "Préférences professionnelles", operationalPrefs: "Préférences opérationnelles", qaPrefs: "Préférences QA", adminPrefs: "Préférences admin", observerPrefs: "Préférences observateur" }, comingSoon: "Bientôt disponible",
  },
  de: {
    panel: "Übersicht", racketLab: "Racket Lab", myStringings: "Meine Bespannungen", reservation: "Bespannung buchen", myGear: "Meine Ausrüstung", myQr: "Mein QR",
    myCalendar: "Mein Kalender", myQuestionnaires: "Meine Fragebögen", wishlists: "Wunschliste", referrals: "Meine Empfehlungen", requestsHub: "Anfragen-Hub", myRewards: "Meine Prämien",
    myProfile: "Mein Profil", opsView: "Operative Ansicht", profile: { account: "Konto", calendar: "Kalender", notifications: "Benachrichtigungen", socialNetworks: "Soziale Netzwerke", familyRelations: "Familie und Beziehungen", favorites: "Favoriten", hobbies: "Hobbys und Interessen", friendsNetwork: "Freunde und Netzwerk", messaging: "Nachrichten", wishlistPrefs: "Wunschlisten-Einstellungen", travelPrefs: "Reise-Einstellungen", profileData: "Profildaten", professionalPrefs: "Berufliche Einstellungen", operationalPrefs: "Operative Einstellungen", qaPrefs: "QA-Einstellungen", adminPrefs: "Admin-Einstellungen", observerPrefs: "Beobachter-Einstellungen" }, comingSoon: "Demnächst",
  },
  "pt-br": {
    panel: "Painel", racketLab: "Racket Lab", myStringings: "Minhas encordoamentos", reservation: "Reserva de encordoamento", myGear: "Meu equipamento", myQr: "Meu QR",
    myCalendar: "Meu calendário", myQuestionnaires: "Meus questionários", wishlists: "Lista de desejos", referrals: "Meus indicados", requestsHub: "Central de solicitações", myRewards: "Minhas recompensas",
    myProfile: "Meu perfil", opsView: "Visão operacional", profile: { account: "Conta", calendar: "Calendário", notifications: "Notificações", socialNetworks: "Redes sociais", familyRelations: "Família e relações", favorites: "Favoritos", hobbies: "Hobbies e interesses", friendsNetwork: "Amigos e rede", messaging: "Mensagens", wishlistPrefs: "Preferências da wishlist", travelPrefs: "Preferências de viagem", profileData: "Dados do perfil", professionalPrefs: "Preferências profissionais", operationalPrefs: "Preferências operacionais", qaPrefs: "Preferências QA", adminPrefs: "Preferências admin", observerPrefs: "Preferências do observador" }, comingSoon: "Em breve",
  },
  ja: {
    panel: "パネル", racketLab: "Racket Lab", myStringings: "マイガット張り", reservation: "ガット張り予約", myGear: "マイギア", myQr: "マイQR",
    myCalendar: "マイカレンダー", myQuestionnaires: "マイアンケート", wishlists: "ウィッシュリスト", referrals: "紹介", requestsHub: "リクエストハブ", myRewards: "マイリワード",
    myProfile: "マイプロフィール", opsView: "オペレーションビュー", profile: { account: "アカウント", calendar: "カレンダー", notifications: "通知", socialNetworks: "SNS", familyRelations: "家族と関係", favorites: "お気に入り", hobbies: "趣味と興味", friendsNetwork: "友人とネットワーク", messaging: "メッセージ", wishlistPrefs: "ウィッシュリスト設定", travelPrefs: "旅行設定", profileData: "プロフィールデータ", professionalPrefs: "プロフェッショナル設定", operationalPrefs: "オペレーション設定", qaPrefs: "QA設定", adminPrefs: "管理者設定", observerPrefs: "オブザーバー設定" }, comingSoon: "近日公開",
  },
  ko: {
    panel: "패널", racketLab: "Racket Lab", myStringings: "내 스트링", reservation: "스트링 예약", myGear: "내 장비", myQr: "내 QR",
    myCalendar: "내 캘린더", myQuestionnaires: "내 설문지", wishlists: "위시리스트", referrals: "내 추천", requestsHub: "요청 허브", myRewards: "내 리워드",
    myProfile: "내 프로필", opsView: "운영 보기", profile: { account: "계정", calendar: "캘린더", notifications: "알림", socialNetworks: "소셜 네트워크", familyRelations: "가족 및 관계", favorites: "즐겨찾기", hobbies: "취미와 관심사", friendsNetwork: "친구 및 네트워크", messaging: "메시지", wishlistPrefs: "위시리스트 환경설정", travelPrefs: "여행 환경설정", profileData: "프로필 데이터", professionalPrefs: "전문가 환경설정", operationalPrefs: "운영 환경설정", qaPrefs: "QA 환경설정", adminPrefs: "관리자 환경설정", observerPrefs: "옵저버 환경설정" }, comingSoon: "출시 예정",
  },
  zh: {
    panel: "面板", racketLab: "Racket Lab", myStringings: "我的穿线", reservation: "穿线预约", myGear: "我的装备", myQr: "我的二维码",
    myCalendar: "我的日历", myQuestionnaires: "我的问卷", wishlists: "心愿单", referrals: "我的推荐", requestsHub: "请求中心", myRewards: "我的奖励",
    myProfile: "我的资料", opsView: "运营视图", profile: { account: "账户", calendar: "日历", notifications: "通知", socialNetworks: "社交网络", familyRelations: "家庭与关系", favorites: "收藏", hobbies: "爱好与兴趣", friendsNetwork: "朋友与网络", messaging: "消息", wishlistPrefs: "心愿单偏好", travelPrefs: "旅行偏好", profileData: "资料数据", professionalPrefs: "专业偏好", operationalPrefs: "运营偏好", qaPrefs: "QA 偏好", adminPrefs: "管理员偏好", observerPrefs: "观察员偏好" }, comingSoon: "即将推出",
  },
  ca: {
    panel: "Tauler", racketLab: "Racket Lab", myStringings: "Els meus encordats", reservation: "Reserva d'encordat", myGear: "El meu equipament", myQr: "El meu QR",
    myCalendar: "El meu calendari", myQuestionnaires: "Els meus qüestionaris", wishlists: "Llista de desitjos", referrals: "Els meus referits", requestsHub: "Centre de sol·licituds", myRewards: "Les meves recompenses",
    myProfile: "El meu perfil", opsView: "Vista operativa", profile: { account: "Compte", calendar: "Calendari", notifications: "Notificacions", socialNetworks: "Xarxes socials", familyRelations: "Família i relacions", favorites: "Preferits", hobbies: "Aficions i interessos", friendsNetwork: "Amics i xarxa", messaging: "Missatgeria", wishlistPrefs: "Preferències de wishlist", travelPrefs: "Preferències de viatge", profileData: "Dades del perfil", professionalPrefs: "Preferències professionals", operationalPrefs: "Preferències operatives", qaPrefs: "Preferències QA", adminPrefs: "Preferències admin", observerPrefs: "Preferències d'observació" }, comingSoon: "Pròximament",
  },
  eu: {
    panel: "Panela", racketLab: "Racket Lab", myStringings: "Nire kordajeak", reservation: "Kordaje erreserba", myGear: "Nire ekipamendua", myQr: "Nire QR",
    myCalendar: "Nire egutegia", myQuestionnaires: "Nire galdetegiak", wishlists: "Nahien zerrenda", referrals: "Nire gomendioak", requestsHub: "Eskaeren zentroa", myRewards: "Nire sariak",
    myProfile: "Nire profila", opsView: "Eragiketa ikuspegia", profile: { account: "Kontua", calendar: "Egutegia", notifications: "Jakinarazpenak", socialNetworks: "Sare sozialak", familyRelations: "Familia eta harremanak", favorites: "Gogokoak", hobbies: "Zaletasunak eta interesak", friendsNetwork: "Lagunak eta sarea", messaging: "Mezularitza", wishlistPrefs: "Nahien hobespenak", travelPrefs: "Bidaia hobespenak", profileData: "Profil datuak", professionalPrefs: "Lanbide-hobespenak", operationalPrefs: "Eragiketa-hobespenak", qaPrefs: "QA hobespenak", adminPrefs: "Admin hobespenak", observerPrefs: "Behatzaile-hobespenak" }, comingSoon: "Laster",
  },
  gl: {
    panel: "Panel", racketLab: "Racket Lab", myStringings: "Os meus encordados", reservation: "Reserva de encordado", myGear: "O meu equipamento", myQr: "O meu QR",
    myCalendar: "O meu calendario", myQuestionnaires: "Os meus cuestionarios", wishlists: "Lista de desexos", referrals: "Os meus referidos", requestsHub: "Centro de solicitudes", myRewards: "As miñas recompensas",
    myProfile: "O meu perfil", opsView: "Vista operativa", profile: { account: "Conta", calendar: "Calendario", notifications: "Notificacións", socialNetworks: "Redes sociais", familyRelations: "Familia e relacións", favorites: "Favoritos", hobbies: "Afeccións e intereses", friendsNetwork: "Amigos e rede", messaging: "Mensaxería", wishlistPrefs: "Preferencias de wishlist", travelPrefs: "Preferencias de viaxe", profileData: "Datos do perfil", professionalPrefs: "Preferencias profesionais", operationalPrefs: "Preferencias operativas", qaPrefs: "Preferencias QA", adminPrefs: "Preferencias admin", observerPrefs: "Preferencias de observación" }, comingSoon: "Proximamente",
  },
};

// Per-profile visibility for every root menu key + section. `null` means
// "follow the wildcard". `true` shows, `false` hides. The wildcard `*`
// defines the default for any profile not explicitly mapped.
//
// Profiles currently mapped explicitly (matches the `_PROFILE_LABELS`
// table in `backend/app/services/boost_broadcast.py`):
//   player, parent_guardian, coach, club_director, federation_manager,
//   sports_doctor, physiotherapist, sport_psychologist, fitness_coach,
//   racket_technician.
//
// Roles (NOT profile_keys) gated separately via STAFF_ROLE_KEYS:
//   admin, staff, qa_manager, qa_tester, institutional_observer.
//
// === DASHBOARD-SIDEMENU-ALL-PROFILES-ADAPT-1 (Feb 2026) ===
// Decisiones de visibilidad (current state · matriz documentada en
// /app/memory/DASHBOARD_SIDEMENU_ALL_PROFILES_ADAPT_1_CLOSEOUT_v1.md).
//
// - `panel`         : visible para todos.
// - `racket_lab`    : explícito sólo para `player`. Otros perfiles
//                     heredan `*` que lo OCULTA. `parent_guardian`,
//                     `racket_technician` y futuros `shop_manager` /
//                     `operator` / `stringer` quedan PENDIENTES de
//                     decisión J/L (parent → Racket Lab del menor;
//                     ops → vista ops dedicada, no la de Player).
// - `calendar`, `wishlists`, `referrals`, `requests_hub`,
//   `rewards`, `my_profile` : visibles por wildcard. Para los perfiles
//   profesionales (coach, club_director, federation_manager,
//   sports_doctor, physiotherapist, sport_psychologist,
//   fitness_coach, racket_technician) queda PENDIENTE confirmar con
//   J/L si `wishlists` / `referrals` / `rewards` son realmente
//   semánticamente apropiados (default conservador: visibles).
//
// Profiles pending integration (shop_manager / operator / stringer /
// etc.) inherit the wildcard `*` until José/Lumia approve their
// per-profile matrix. Extender sin tocar el layout.
const SECTIONS = {
  PANEL: "panel",
  RACKET_LAB: "racket_lab",
  CALENDAR: "calendar",
  QUESTIONNAIRES: "questionnaires",
  WISHLISTS: "wishlists",
  REFERRALS: "referrals",
  REQUESTS_HUB: "requests_hub",
  REWARDS: "rewards",
  MY_PROFILE: "my_profile",
};

const PROFILE_VISIBILITY = {
  "*": {
    [SECTIONS.PANEL]: true,
    [SECTIONS.RACKET_LAB]: false,
    [SECTIONS.CALENDAR]: true,
    [SECTIONS.QUESTIONNAIRES]: true,
    [SECTIONS.WISHLISTS]: true,
    [SECTIONS.REFERRALS]: true,
    [SECTIONS.REQUESTS_HUB]: true,
    [SECTIONS.REWARDS]: true,
    [SECTIONS.MY_PROFILE]: true,
  },
  player: { [SECTIONS.RACKET_LAB]: true },
  // RACKET-LAB-PROFILES-FINAL-PREVIEW-ALIGN-1: ops profiles see the
  // Racket Lab section with a single `Próximamente` placeholder
  // (`opsView`). Real operational console lives at /admin/racket-lab/*
  // and is gated by the existing admin RBAC (SS-8/CP-1.3). The user-side
  // placeholder advertises the upcoming dedicated personal/ops view.
  // PENDING decisions documented in
  // /app/memory/DASHBOARD_SIDEMENU_ALL_PROFILES_ADAPT_1_CLOSEOUT_v1.md.
  parent_guardian: {},
  coach: {},
  head_coach: {},
  club_director: {},
  federation_manager: {},
  fitness_coach: {},
  sports_doctor: {},
  physiotherapist: {},
  sport_psychologist: {},
  racket_technician:  { [SECTIONS.RACKET_LAB]: true },
  shop_manager:       { [SECTIONS.RACKET_LAB]: true },
  operator:           { [SECTIONS.RACKET_LAB]: true },
  stringer:           { [SECTIONS.RACKET_LAB]: true },  // legacy alias
  // QA/observer/admin profiles are gated by `role` via STAFF_ROLE_KEYS
  // for the rewards entry; the rest follow the wildcard. They access
  // Racket Lab via the admin console at /admin/racket-lab/* (SS-8).
  qa_manager: {},
  qa_tester: {},
  institutional_observer: {},
};

// RACKET-LAB-PROFILES-FINAL-PREVIEW-ALIGN-1 — items rendered inside
// the Racket Lab section, per profile_key / role. Player keeps its
// 4-item canonical baseline (validated in CONTROLLED-PILOT-READINESS-1).
// Ops profiles see a single `opsView` placeholder.
//
// Subitem schema: { key, labelKey, icon, to, testid, comingSoon }
//   - labelKey  → resolves against `T.{lang}.<labelKey>` or `T.{lang}.profile.<labelKey>`
//   - to        → either a real route OR `/app/racket-lab/coming-soon` for placeholders
//   - comingSoon → renders the `Próximamente` badge; route renders `MyRacketLabComingSoon`
const PLAYER_RL_ITEMS = [
  { key: "stringings",  labelKey: "myStringings", icon: ListChecks, to: "/app/my-rackets",           testid: "side-menu-my-stringings", comingSoon: false },
  { key: "reservation", labelKey: "reservation",  icon: Store,      to: "/app/racket-lab/reserva",   testid: "side-menu-reservation",   comingSoon: false },
  { key: "gear",        labelKey: "myGear",       icon: Package,    to: "/app/racket-lab/equipment", testid: "side-menu-my-gear",       comingSoon: false },
  { key: "qr",          labelKey: "myQr",         icon: QrCode,     to: "/app/racket-lab/qr",        testid: "side-menu-my-qr",         comingSoon: false },
];

const OPS_RL_ITEMS = [
  // FIX FINAL-DEMO-PROFILES-QUESTIONNAIRES-RL-OPS-FIX-1 (Feb 2026):
  // `/app/racket-lab/ops` is now a Preview-controlled dashboard
  // (KPI cards + ticket queue + disabled CTA), no longer a Próximamente
  // placeholder. The `comingSoon` flag drives the sidebar badge — set
  // to false so we don't advertise it as upcoming.
  { key: "opsView", labelKey: "opsView", icon: Wrench, to: "/app/racket-lab/ops", testid: "side-menu-rl-ops-view", comingSoon: false },
];

const RACKET_LAB_ITEMS_BY_KEY = {
  player:            PLAYER_RL_ITEMS,
  racket_technician: OPS_RL_ITEMS,
  shop_manager:      OPS_RL_ITEMS,
  operator:          OPS_RL_ITEMS,
  stringer:          OPS_RL_ITEMS,
};

function racketLabItemsFor(user) {
  if (!user) return [];
  const key = STAFF_ROLE_KEYS.has(user.role) ? user.role : (user.profile_key || "*");
  return RACKET_LAB_ITEMS_BY_KEY[key] || [];
}

function visibleFor(profileKey, sectionKey) {
  const explicit = PROFILE_VISIBILITY[profileKey] || {};
  if (Object.prototype.hasOwnProperty.call(explicit, sectionKey)) {
    return Boolean(explicit[sectionKey]);
  }
  return Boolean(PROFILE_VISIBILITY["*"][sectionKey]);
}

// Settings page sub-sections — kept in lockstep with
// `MySettings.jsx::SECTION_ORDER`. `comingSoon` mirrors what the
// Settings page actually renders so we never advertise a tab the user
// can interact with as "Próximamente".
//
// PROFILE-SETTINGS-ALL-PROFILES-ALIGN-1 (Feb 2026) — matrix below
// (`SETTINGS_VISIBILITY_BY_KEY`) drives which of these subitems each
// profile_key / staff role actually sees. Player keeps the 12 original
// entries (validated baseline). Professional / operational / QA /
// admin / observer profiles inherit a tailored subset + one own
// `Próximamente` placeholder so each role gets a coherent panel.
//
// New placeholder subitems are ALL `comingSoon: true` (legal hard-block
// — no real role-specific settings backend exists yet). Pending GO from
// José/Lumia to define real per-role preferences.
const PROFILE_SUBITEMS = [
  { key: "account",          hash: "#account",        comingSoon: false },
  { key: "calendar",         hash: "#calendar",       comingSoon: false },
  { key: "notifications",    hash: "#notifications",  comingSoon: false },
  { key: "socialNetworks",   hash: "#social",         comingSoon: false },
  { key: "familyRelations",  hash: "#family",         comingSoon: true  },
  { key: "favorites",        hash: "#favorites",      comingSoon: false },
  { key: "hobbies",          hash: "#hobbies",        comingSoon: false },
  { key: "friendsNetwork",   hash: "#friends",        comingSoon: true  },
  { key: "messaging",        hash: "#messaging",      comingSoon: true  },
  { key: "wishlistPrefs",    hash: "#wishlist-prefs", comingSoon: false },
  { key: "travelPrefs",      hash: "#travel-prefs",   comingSoon: false },
  { key: "profileData",      hash: "#profile-data",   comingSoon: false },
  // Per-role Próximamente placeholders (PROFILE-SETTINGS-ALL-PROFILES-ALIGN-1)
  { key: "professionalPrefs",hash: "#pro-prefs",      comingSoon: true  },
  // FINAL-DEMO-PROFILES-QUESTIONNAIRES-RL-OPS-FIX-1 (Feb 2026):
  // operationalPrefs is no longer a placeholder — `MySettings.jsx`
  // renders `OperationalPrefsPreviewSection` (Preview-controlled,
  // read-only). Drop the sidebar badge to match.
  { key: "operationalPrefs", hash: "#ops-prefs",      comingSoon: false },
  { key: "qaPrefs",          hash: "#qa-prefs",       comingSoon: true  },
  { key: "adminPrefs",       hash: "#admin-prefs",    comingSoon: true  },
  { key: "observerPrefs",    hash: "#observer-prefs", comingSoon: true  },
];

// Per-profile visible-subitem matrix.
// Key resolution order (see `resolveSettingsKey` below):
//   1) `user.role` if it lives in STAFF_ROLE_KEYS (admin/staff/qa_*/observer)
//   2) `user.profile_key` otherwise
//   3) wildcard `*` as default
//
// Each list is the ORDERED subset of PROFILE_SUBITEMS that should
// appear in the Mi perfil submenu for that key. Player's list is the
// canonical 12-entry baseline (validated in CONTROLLED-PILOT-READINESS-1).
const PLAYER_SUBITEMS = [
  "account", "calendar", "notifications", "socialNetworks",
  "familyRelations", "favorites", "hobbies", "friendsNetwork",
  "messaging", "wishlistPrefs", "travelPrefs", "profileData",
];

const PERSONAL_TRACK = [
  "account", "calendar", "notifications", "socialNetworks",
  "familyRelations", "favorites", "hobbies", "friendsNetwork",
  "messaging", "wishlistPrefs", "travelPrefs", "profileData",
];

const PROFESSIONAL_TRACK = [
  "account", "calendar", "notifications", "socialNetworks",
  "friendsNetwork", "messaging", "travelPrefs", "profileData",
  "professionalPrefs",
];

const OPERATIONAL_TRACK = [
  "account", "calendar", "notifications", "socialNetworks",
  "friendsNetwork", "messaging", "profileData",
  "operationalPrefs",
];

const QA_TRACK = [
  "account", "notifications", "socialNetworks",
  "messaging", "profileData",
  "qaPrefs",
];

const ADMIN_TRACK = [
  "account", "notifications", "socialNetworks",
  "messaging", "profileData",
  "adminPrefs",
];

const OBSERVER_TRACK = [
  "account", "notifications",
  "observerPrefs",
];

const SETTINGS_VISIBILITY_BY_KEY = {
  "*":                    PERSONAL_TRACK,
  // Personal-track profiles
  player:                 PLAYER_SUBITEMS,
  parent_guardian:        PERSONAL_TRACK,
  // Professional-track profiles
  coach:                  PROFESSIONAL_TRACK,
  head_coach:             PROFESSIONAL_TRACK,
  club_director:          PROFESSIONAL_TRACK,
  federation_manager:     PROFESSIONAL_TRACK,
  fitness_coach:          PROFESSIONAL_TRACK,
  sports_doctor:          PROFESSIONAL_TRACK,
  physiotherapist:        PROFESSIONAL_TRACK,
  sport_psychologist:     PROFESSIONAL_TRACK,
  // Operational-track profiles
  racket_technician:      OPERATIONAL_TRACK,
  shop_manager:           OPERATIONAL_TRACK,  // RL role · pending PLAN
  operator:               OPERATIONAL_TRACK,  // RL role · pending PLAN
  stringer:               OPERATIONAL_TRACK,  // RL role legacy · pending PLAN
  // Staff / QA / Admin / Observer — keyed by `user.role`
  admin:                  ADMIN_TRACK,
  staff:                  ADMIN_TRACK,
  qa_manager:             QA_TRACK,
  qa_tester:              QA_TRACK,
  institutional_observer: OBSERVER_TRACK,
};

function resolveSettingsKey(user) {
  if (!user) return "*";
  if (user.role && STAFF_ROLE_KEYS.has(user.role)) return user.role;
  return user.profile_key || "*";
}

function visibleSubitemsFor(user) {
  const key = resolveSettingsKey(user);
  const list = SETTINGS_VISIBILITY_BY_KEY[key] || SETTINGS_VISIBILITY_BY_KEY["*"];
  const byKey = Object.fromEntries(PROFILE_SUBITEMS.map((s) => [s.key, s]));
  return list.map((k) => byKey[k]).filter(Boolean);
}

const ITEM_BASE  = "flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium transition";
const ITEM_IDLE  = "text-slate-700 hover:bg-emerald-50 hover:text-emerald-800";
const ITEM_ACTIVE= "bg-emerald-600 text-white shadow-sm";
const SUB_BASE   = "flex items-center justify-between gap-2 rounded-md ps-7 pe-2.5 py-1.5 text-[13px] transition";
const SUB_IDLE   = "text-slate-600 hover:bg-emerald-50 hover:text-emerald-800";
const SUB_ACTIVE = "bg-emerald-100 text-emerald-900 font-semibold";

function SectionTitle({ icon: Icon, label, testid }) {
  return (
    <div
      role="presentation"
      className="flex items-center gap-2 px-2.5 pt-2 pb-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700 select-none cursor-default"
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

export default function DashboardSideMenu() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tx = T[lang] || T.en;
  const location = useLocation();

  const profileKey = user?.profile_key || "*";
  const showRewards =
    visibleFor(profileKey, SECTIONS.REWARDS) &&
    user && !STAFF_ROLE_KEYS.has(user.role);
  const showRacketLab = visibleFor(profileKey, SECTIONS.RACKET_LAB);
  const showProfile   = visibleFor(profileKey, SECTIONS.MY_PROFILE);
  const showCalendar  = visibleFor(profileKey, SECTIONS.CALENDAR);
  const showWishlists = visibleFor(profileKey, SECTIONS.WISHLISTS);
  const showReferrals = visibleFor(profileKey, SECTIONS.REFERRALS);
  const showRequests  = visibleFor(profileKey, SECTIONS.REQUESTS_HUB);
  const showQuestionnaires = visibleFor(profileKey, SECTIONS.QUESTIONNAIRES);

  // FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1: collapse Mi perfil subitems
  // when navigating outside the settings page so the sidebar stays
  // compact. Section title still visible (so users know how to enter).
  const isOnSettings = location.pathname.startsWith("/app/settings");

  const fullName = user?.full_name || user?.email || "";

  return (
    <aside
      className="tsw-card p-3 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto"
      data-testid="dashboard-side-menu"
      aria-label="Dashboard navigation"
      style={{ scrollbarWidth: "thin" }}
    >
      <div className="px-1 mb-3" data-testid="dashboard-side-menu-header">
        <div
          className="tsw-overline text-emerald-700"
          data-testid="dashboard-side-menu-eyebrow"
        >
          {tx.panelEyebrow || tx.panel}
        </div>
        <div
          className="font-display font-semibold text-slate-900 text-base leading-tight mt-0.5 truncate"
          title={fullName}
          data-testid="dashboard-side-menu-username"
        >
          {fullName}
        </div>
      </div>

      <nav className="space-y-0.5" data-testid="dashboard-side-menu-nav">
        {/* `Panel` — universal return point to /app. Interactive root
            entry above every other section. */}
        <NavLink
          to="/app"
          end
          className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
          data-testid="side-menu-panel"
        >
          <Home className="w-4 h-4 shrink-0" />
          <span className="truncate font-semibold">{tx.panel}</span>
        </NavLink>

        {/* Racket Lab — STATIC section title + per-profile filtered items.
            RACKET-LAB-PROFILES-FINAL-PREVIEW-ALIGN-1: the item list is
            now driven by `RACKET_LAB_ITEMS_BY_KEY`. Player keeps the
            canonical 4-entry baseline. Ops profiles see an `opsView`
            placeholder (Próximamente). All others see no RL section. */}
        {showRacketLab && (() => {
          const rlItems = racketLabItemsFor(user);
          if (rlItems.length === 0) return null;
          return (
            <>
              <div className="my-1 h-px bg-slate-100" />
              <SectionTitle
                icon={Wrench}
                label={tx.racketLab}
                testid="side-menu-section-racket-lab"
              />
              <div className="space-y-0.5" data-testid="side-menu-section-racket-lab-items">
                {rlItems.map(({ key, labelKey, icon: Icon, to, testid, comingSoon }) => {
                  const label = tx[labelKey] || labelKey;
                  return (
                    <NavLink
                      key={key}
                      to={to}
                      end
                      className={({ isActive }) =>
                        `${SUB_BASE} ${isActive ? SUB_ACTIVE : SUB_IDLE}`
                      }
                      data-testid={testid}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{label}</span>
                      </span>
                      {comingSoon && (
                        <span
                          className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap"
                          data-testid={`${testid}-soon`}
                        >
                          {tx.comingSoon}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* Intermediate flat entries */}
        <div className="my-1 h-px bg-slate-100" />
        {showCalendar && (
          <NavLink
            to="/app/calendar"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-my-calendar"
          >
            <CalendarDays className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.myCalendar}</span>
          </NavLink>
        )}
        {/* FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1 addendum v1.2: Mis cuestionarios
            ya es una sección activa del panel del usuario. Sin badge
            PRÓXIMAMENTE. Empty state explicativo gestionado por la
            propia página. */}
        {showQuestionnaires && (
          <NavLink
            to="/app/questionnaires"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-my-questionnaires"
          >
            <ListChecks className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.myQuestionnaires}</span>
          </NavLink>
        )}
        {showWishlists && (
          <NavLink
            to="/app/wishlists"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-wishlists"
          >
            <Heart className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.wishlists}</span>
          </NavLink>
        )}
        {showReferrals && (
          <NavLink
            to="/app/referrals"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-referrals"
          >
            <Share2 className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.referrals}</span>
          </NavLink>
        )}
        {showRequests && (
          <NavLink
            to="/app/marketplace-requests"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-requests-hub"
          >
            <Inbox className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.requestsHub}</span>
          </NavLink>
        )}
        {showRewards && (
          <NavLink
            to="/app/my-rewards"
            end
            className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
            data-testid="side-menu-my-rewards"
          >
            <Award className="w-4 h-4 shrink-0" />
            <span className="truncate">{tx.myRewards}</span>
          </NavLink>
        )}

        {/* `Mi perfil` — STATIC section title + per-profile filtered subitems.
            PROFILE-SETTINGS-ALL-PROFILES-ALIGN-1 (Feb 2026): the list is
            now driven by `SETTINGS_VISIBILITY_BY_KEY` so each profile
            sees only the subitems relevant to its role. Player keeps
            the canonical 12-entry baseline. */}
        {showProfile && (
          <>
            <div className="my-1 h-px bg-slate-100" />
            <NavLink
              to="/app/settings#account"
              className={({ isActive }) => {
                const active = isOnSettings;
                return `flex items-center gap-2 px-2.5 pt-2 pb-1 text-[11px] font-bold uppercase tracking-widest ${active ? "text-emerald-900" : "text-emerald-700 hover:text-emerald-900"} select-none`;
              }}
              data-testid="side-menu-section-my-profile"
            >
              <SettingsIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{tx.myProfile}</span>
            </NavLink>
            {/* FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1: only show subitems
                when the user is actually inside /app/settings/* (avoids
                a 12-item dropdown polluting the sidebar in every
                other view). The section title above remains visible
                and acts as the gateway link → /app/settings#account. */}
            {isOnSettings && (
              <div className="space-y-0.5" data-testid="side-menu-section-my-profile-items">
                {visibleSubitemsFor(user).map(({ key, hash, comingSoon }) => {
                  const label = tx.profile[key] || (T.en.profile && T.en.profile[key]) || key;
                  const testid = `side-menu-profile-${key}`;
                  const hashMatches = (location.hash || "") === hash;
                  return (
                    <NavLink
                      key={key}
                      to={`/app/settings${hash}`}
                      className={`${SUB_BASE} ${hashMatches ? SUB_ACTIVE : SUB_IDLE}`}
                      data-testid={testid}
                    >
                      <span className="truncate">{label}</span>
                      {comingSoon && (
                        <span
                          className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap"
                          data-testid={`${testid}-soon`}
                        >
                          {tx.comingSoon}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}
      </nav>
    </aside>
  );
}
